import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, ShieldCheck, TrendingUp, Store, Layers,
  Percent, Boxes, Zap, Sparkles, Lock, Workflow, Database, Users, FileCheck,
  Radio, Globe, HeartHandshake, Calendar,
} from "lucide-react";
import { commerceSite, commerceDemoSummary, commerceDemoRecs, commerceReuseAdapters } from "@/lib/api";

const ICONS = {
  "profit-center": TrendingUp,
  "competitor-monitor": Store,
  "smart-pricing": Percent,
  "promotion-optimizer": Sparkles,
  "inventory-demand": Boxes,
  "action-center": Zap,
};

export default function AiCommerceIntelligence() {
  const [site, setSite] = useState(null);
  const [demo, setDemo] = useState(null);
  const [recs, setRecs] = useState([]);
  const [adapters, setAdapters] = useState([]);
  useEffect(() => {
    document.title = "AI Commerce Intelligence · WebsiteTech.pro";
    commerceSite().then(setSite).catch(() => {});
    commerceDemoSummary().then(setDemo).catch(() => {});
    commerceDemoRecs().then((d) => setRecs(d.items || [])).catch(() => {});
    commerceReuseAdapters().then((d) => setAdapters(d.adapters || [])).catch(() => {});
  }, []);
  if (!site) return <div className="pt-32 text-center text-slate-600">Loading…</div>;

  const brand = site.brand;
  return (
    <div className="min-h-screen bg-[#fdfbf5] text-slate-900" data-testid="commerce-marketing">
      {/* Hero */}
      <section className="relative overflow-hidden pt-24 pb-20 border-b border-[#ebe4d3]">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-700 font-bold border border-emerald-400/30 bg-emerald-500/10 rounded-full px-3 py-1"
            data-testid="commerce-hero-badge">
            <TrendingUp className="w-3 h-3" /> WebsiteTech AI · Commerce Intelligence
          </div>
          <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05]"
            data-testid="commerce-hero-headline">
            {brand.headline}
          </h1>
          <p className="mt-6 text-lg text-slate-700 max-w-2xl leading-relaxed">{brand.supporting}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/portal/commerce" data-testid="commerce-hero-cta-portal"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-6 py-3.5 rounded-lg font-bold transition shadow-lg shadow-emerald-500/20">
              Open the demo dashboard <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:sales@websitetech.pro?subject=Commerce%20Profit%20Audit"
              data-testid="commerce-hero-cta-audit"
              className="inline-flex items-center gap-2 border border-[#ebe4d3] hover:border-white/40 bg-white hover:bg-white/10 px-6 py-3.5 rounded-lg font-semibold transition">
              Request a commerce-profit audit <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          {/* Live demo stat strip */}
          {demo && (
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="commerce-demo-stats">
              <StatTile label="Demo revenue (30d)" value={`₹ ${Math.round(demo.revenue).toLocaleString("en-IN")}`} />
              <StatTile label="Gross margin" value={`${demo.gross_margin_pct}%`} accent="emerald" />
              <StatTile label="Orders" value={demo.orders} />
              <StatTile label="Active recs" value={recs.length} accent="amber" />
            </div>
          )}
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 border-b border-[#ebe4d3] bg-gradient-to-b from-transparent to-rose-950/20">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-rose-700 font-bold">The problem</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold">Your data already knows where profit leaks. Nobody's asking it.</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Ecommerce stores, retailers, wholesalers, restaurants and food businesses generate more product, pricing and competitor data than any person can act on. Analytics dashboards summarize the past — they don't tell you what to do next. Margin quietly bleeds through mispriced SKUs, stale competitor gaps, dead stock and unnecessary discounts.
            </p>
          </div>
          <div className="space-y-3">
            {[
              "Best-sellers silently losing margin as costs rise",
              "Competitor undercuts detected days late — after the sales are lost",
              "Aging inventory sitting on shelves while cash flow tightens",
              "Blanket discounts eroding profit on items that would have sold anyway",
              "Stockouts on winners while dead stock ties up working capital",
            ].map((p) => (
              <div key={p} className="flex items-start gap-3 rounded-lg bg-rose-500/[0.05] border border-rose-400/20 p-4"
                data-testid="commerce-problem-item">
                <div className="w-2 h-2 rounded-full bg-rose-400 mt-2 flex-shrink-0" />
                <div className="text-sm text-slate-800">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 border-b border-[#ebe4d3]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-bold">How it works</div>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">Detect → Explain → Recommend → Approve → Execute → Measure → Learn.</h2>
          <p className="mt-4 text-slate-600 max-w-3xl">A continuous decision loop, not another passive dashboard. Every step is inspectable, reversible, and captured in the audit trail.</p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { i: Database, k: "Connect", t: "Shopify · WooCommerce · CSV · XLSX · PDF price lists" },
              { i: Radio, k: "Detect", t: "Margin drifts, competitor moves, stock risks, aging SKUs" },
              { i: FileCheck, k: "Explain", t: "Every rec cites data, assumptions, expected impact + confidence" },
              { i: ShieldCheck, k: "Guardrail", t: "Margin floors, max-change caps, protected SKUs, blackout dates" },
              { i: Users, k: "Approve", t: "Assign, modify, reject with reason — required for learning" },
              { i: Zap, k: "Execute", t: "Staged locally · external publishing is a Phase 3 opt-in" },
              { i: TrendingUp, k: "Measure", t: "Realised vs projected profit — backtested against baselines" },
              { i: Workflow, k: "Learn", t: "Rejections + modifications teach the engine over time" },
            ].map(({ i: Icon, k, t }) => (
              <div key={k} className="rounded-xl border border-[#ebe4d3] bg-white p-5"
                data-testid={`commerce-how-${k.toLowerCase()}`}>
                <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-400/30 grid place-items-center">
                  <Icon className="w-4 h-4 text-emerald-700" />
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-600 font-bold">Step</div>
                <div className="text-base font-bold">{k}</div>
                <p className="mt-2 text-xs text-slate-600 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 modules */}
      <section className="py-24 border-b border-[#ebe4d3]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-bold">The engine</div>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">Six modules that turn raw data into decisions.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {site.modules.map((m) => {
              const I = ICONS[m.key] || Layers;
              return (
                <div key={m.key} className="rounded-xl border border-[#ebe4d3] bg-white hover:bg-white/[0.06] p-6 transition"
                  data-testid={`commerce-module-${m.key}`}>
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-400/30 grid place-items-center">
                    <I className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div className="mt-4 text-[10px] uppercase tracking-widest text-slate-600 font-bold">{m.kicker}</div>
                  <div className="mt-1 text-lg font-bold">{m.title}</div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{m.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Live recs preview */}
      {recs.length > 0 && (
        <section className="py-24 border-b border-[#ebe4d3] bg-gradient-to-b from-transparent to-emerald-900/10">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-bold">Live recommendation feed · demo tenant</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">This is what your Action Center will look like.</h2>
            <p className="mt-3 text-slate-600 max-w-2xl">Every recommendation shows what changed, why it matters, expected impact and confidence — approve, modify or reject in one click. Nothing publishes without you.</p>
            <div className="mt-10 space-y-3">
              {recs.slice(0, 4).map((r) => (
                <div key={r.id} className="rounded-xl border border-[#ebe4d3] bg-white p-5"
                  data-testid={`commerce-rec-preview-${r.id}`}>
                  <div className="flex flex-wrap items-start gap-3">
                    <div className={`text-[9px] uppercase tracking-widest font-bold rounded px-2 py-1 ${
                      r.kind === "margin-floor" ? "bg-amber-500/15 text-amber-700 border border-amber-400/30" :
                      r.kind === "competitor-match" ? "bg-indigo-500/15 text-indigo-700 border border-indigo-400/30" :
                      r.kind === "clear-aging-stock" ? "bg-rose-500/15 text-rose-700 border border-rose-400/30" :
                      "bg-emerald-500/15 text-emerald-700 border border-emerald-400/30"
                    }`}>{r.kind}</div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-slate-600">confidence · {(r.confidence * 100).toFixed(0)}%</div>
                    <div className="ml-auto text-xs text-slate-600">SKU {r.sku}</div>
                  </div>
                  <div className="mt-3 text-lg font-semibold">{r.title}</div>
                  <div className="mt-1 text-sm text-slate-600">{r.why}</div>
                  {r.expected_impact && (r.expected_impact.gross_profit_delta || r.expected_impact.prevents_stockout) && (
                    <div className="mt-3 text-xs text-emerald-700 font-mono">
                      {r.expected_impact.prevents_stockout
                        ? `Prevents stockout · reorder ${r.expected_impact.units} units`
                        : `Est. profit delta ₹ ${Math.round(r.expected_impact.gross_profit_delta).toLocaleString("en-IN")} over 30d`}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/portal/commerce" data-testid="commerce-recs-cta"
                className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-200 font-bold text-sm">
                Explore the full Action Center → <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Safeguards */}
      <section className="py-24 border-b border-[#ebe4d3]">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-bold">Built with guardrails</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold">You stay in control. Always.</h2>
            <p className="mt-3 text-slate-600">The MVP never auto-publishes a price. Every action needs approval. Every approval is logged. Every executed change can be rolled back.</p>
          </div>
          <div className="lg:col-span-2 space-y-3">
            {site.safeguards.map((s) => (
              <div key={s} className="flex items-start gap-3 rounded-lg bg-white border border-[#ebe4d3] p-4"
                data-testid="commerce-safeguard">
                <ShieldCheck className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-800">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-24 border-b border-[#ebe4d3]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-bold">Packages</div>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">Start monitoring. Grow into optimizing. Have us manage it.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {site.packages.map((pkg) => (
              <div key={pkg.key}
                data-testid={`commerce-pkg-${pkg.key}`}
                className={`rounded-2xl border p-6 ${pkg.recommended
                    ? "border-emerald-400/60 bg-emerald-500/[0.08] shadow-2xl shadow-emerald-500/10"
                    : "border-[#ebe4d3] bg-white"}`}>
                {pkg.recommended && (
                  <div className="mb-3 inline-flex text-[10px] uppercase tracking-widest font-bold text-emerald-700 border border-emerald-400/40 bg-emerald-500/15 rounded-full px-2 py-0.5">
                    Recommended
                  </div>
                )}
                <div className="text-xl font-bold">{pkg.name}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{pkg.positioning}</p>
                <ul className="mt-6 space-y-2">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-800">
                      <CheckCircle2 className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <a href={`mailto:sales@websitetech.pro?subject=${encodeURIComponent(pkg.name)}`}
                  data-testid={`commerce-pkg-cta-${pkg.key}`}
                  className={`mt-6 inline-flex items-center gap-2 rounded-lg px-4 py-2.5 font-semibold text-sm transition ${
                    pkg.recommended
                      ? "bg-emerald-500 hover:bg-emerald-400 text-slate-900"
                      : "border border-[#ebe4d3] hover:border-white/40 bg-white hover:bg-white/10 text-white"
                  }`}>
                  {pkg.cta} <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries + FAQs */}
      <section className="py-24 border-b border-[#ebe4d3]">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-bold">Who this is for</div>
            <h3 className="mt-2 text-2xl md:text-3xl font-bold">Product-based businesses that live and die by margin.</h3>
            <div className="mt-6 flex flex-wrap gap-2">
              {site.industries.map((i) => (
                <span key={i} className="text-xs font-semibold rounded-full border border-[#ebe4d3] bg-white px-3 py-1.5">{i}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-bold">FAQ</div>
            <h3 className="mt-2 text-2xl md:text-3xl font-bold">Questions we always get.</h3>
            <div className="mt-6 space-y-3">
              {site.faqs.map((f) => (
                <details key={f.q} className="rounded-lg border border-[#ebe4d3] bg-white p-4"
                  data-testid="commerce-faq">
                  <summary className="cursor-pointer font-semibold text-sm">{f.q}</summary>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cross-platform reuse */}
      {adapters.length > 0 && (
        <section className="py-24 border-b border-[#ebe4d3] bg-gradient-to-b from-transparent to-indigo-950/20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-xs uppercase tracking-[0.3em] text-indigo-700 font-bold">One engine · many domains</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">The same engine powers commerce across the Social Pulse network.</h2>
            <p className="mt-3 text-slate-600 max-w-2xl">Strict per-tenant isolation is enforced. No cross-client data is used to power another client's recommendations.</p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {adapters.map((a) => (
                <div key={a.key} className="rounded-xl border border-[#ebe4d3] bg-white p-5"
                  data-testid={`commerce-adapter-${a.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="w-9 h-9 rounded-lg bg-indigo-500/10 border border-indigo-400/30 grid place-items-center">
                      <Globe className="w-4 h-4 text-indigo-700" />
                    </div>
                    <span className={`text-[9px] uppercase tracking-widest font-bold rounded px-2 py-0.5 border ${
                      a.status === "beta" ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-700" :
                      "border-slate-400/30 bg-slate-500/10 text-slate-700"
                    }`}>{a.status}</span>
                  </div>
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-600 font-bold">{a.domain}</div>
                  <div className="text-base font-bold">{a.key.replace(/_/g, " ")}</div>
                  <p className="mt-2 text-xs text-slate-600 leading-relaxed">{a.positioning}</p>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {(a.capabilities || []).slice(0, 3).map((c) => (
                      <span key={c} className="text-[10px] font-mono text-slate-500 border border-[#ebe4d3] rounded px-1.5 py-0.5">
                        {c.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Book consultation strip */}
      <section className="py-16 border-b border-[#ebe4d3] bg-gradient-to-r from-emerald-500/[0.06] via-transparent to-indigo-500/[0.06]">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-bold flex items-center gap-1.5">
              <HeartHandshake className="w-3 h-3" /> Consultation
            </div>
            <h3 className="mt-2 text-2xl md:text-3xl font-bold">Book a 30-min commerce-profit consultation.</h3>
            <p className="mt-3 text-sm text-slate-600">Walk us through your top 5 SKUs, current margin pressures and competitor set. Leave with a first-look opportunity map — no obligation.</p>
          </div>
          <div className="space-y-3">
            <a href="mailto:sales@websitetech.pro?subject=Commerce%20Intelligence%20Consultation"
              data-testid="commerce-book-consult"
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-lg px-6 py-3 font-bold transition">
              <Calendar className="w-4 h-4" /> Book a discovery call <ArrowRight className="w-4 h-4" />
            </a>
            <a href="mailto:sales@websitetech.pro?subject=Commerce%20Profit%20Audit%20Request"
              data-testid="commerce-request-audit"
              className="w-full inline-flex items-center justify-center gap-2 border border-[#ebe4d3] hover:border-white/40 bg-white hover:bg-white/10 rounded-lg px-6 py-3 font-semibold transition">
              <FileCheck className="w-4 h-4" /> Request a commerce-profit audit
            </a>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Lock className="w-8 h-8 text-emerald-700 mx-auto" />
          <h2 className="mt-4 text-3xl md:text-4xl font-bold">Ready to see what your data is worth?</h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">Connect Shopify, WooCommerce or upload a CSV. We'll show you the top 5 profit opportunities in under 10 minutes.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/portal/commerce" data-testid="commerce-cta-open-dashboard"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-6 py-3 rounded-lg font-bold transition shadow-lg shadow-emerald-500/20">
              Open the dashboard <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:sales@websitetech.pro?subject=Book%20a%20commerce%20discovery%20call"
              className="inline-flex items-center gap-2 border border-[#ebe4d3] hover:border-white/40 bg-white px-6 py-3 rounded-lg font-semibold transition">
              Book a discovery call
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatTile({ label, value, accent = "slate" }) {
  const color = {
    slate: "text-slate-900", emerald: "text-emerald-700", amber: "text-amber-700",
  }[accent] || "text-slate-900";
  return (
    <div className="rounded-2xl border border-[#ebe4d3] bg-white p-5 shadow-sm">
      <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className={`mt-2 text-3xl font-bold tracking-tight ${color}`}>{value}</div>
    </div>
  );
}
